import { Component, DestroyRef, effect, EventEmitter, inject, Injector, Input, OnInit, Output, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption, ThemePalette } from '@angular/material/core';
import { FloatLabelType, MatFormField, MatFormFieldAppearance, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Observable } from 'rxjs';
import { PayerItem } from '../../../models/payer-item';
import { FieldFilterService } from '../../../services/filters/field-filter.service';
import { AppStore } from '../../../store/app.store';

// New-look replacement for app-payer-select. Matches payers by ID, so the selection survives an option reload.
@Component({
  selector: 'app-payer-filter-select',
  templateUrl: './payer-filter-select.component.html',
  styleUrls: ['./payer-filter-select.component.scss'],
  imports: [MatFormField, MatLabel, MatSelect, MatSelectTrigger, MatOption, MatTooltip, ReactiveFormsModule, NgxMatSelectSearchModule]
})
export class PayerFilterSelectComponent implements OnInit {

  private readonly filterService = inject(FieldFilterService);
  private readonly appStore = inject(AppStore);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _disabled = signal(false);
  @Input() set disabled(value: boolean) {
    this._disabled.set(value);
  }

  @Input() hidden = false;
  @Input() form: FormGroup | null = null;
  @Input() controlName = 'payer';
  @Input() label = 'Payer';
  @Input() color: ThemePalette = 'primary';
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() floatLabel: FloatLabelType = 'always';
  @Input() multiple = true;
  @Input() selectAllEnabled = true;
  @Input() required = false;
  @Input() placeholder = 'Make a Selection';
  @Input() toolTipPosition: TooltipPosition = 'above';
  @Input() hideSubscriptWrapper = false;
  // queryOptions loads on init; the flags pick the endpoint (all payers by default).
  @Input() queryOptions = false;
  @Input() filterByMORMap = false;
  @Input() filterByAsmOnServiceYear = false;
  // Adds inactive payers to the all-payers list.
  @Input() includeInactive = false;

  @Output() ready = new EventEmitter<void>();
  // Emitted when the panel closes.
  @Output() valueChanged = new EventEmitter<any>();

  readonly isReady = signal(false);
  readonly hasSelection = signal(false);

  options: PayerItem[] = [];
  filteredOptions: PayerItem[] = [];
  readonly control = new FormControl<any>(null);
  readonly filterControl = new FormControl<string>('', { nonNullable: true });

  readonly compareById = (a: PayerItem, b: PayerItem) => a?.ID === b?.ID;

  ngOnInit(): void {
    if (this.required) {
      this.control.setValidators([Validators.required]);
    }

    this.form?.addControl(this.controlName, this.control);

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this._disabled()) {
          this.control.disable({ emitEvent: false });
        } else {
          this.control.enable({ emitEvent: false });
        }
      });
    });

    this.filterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applySearch());

    if (this.queryOptions) {
      if (this.filterByAsmOnServiceYear) {
        // Reloads when the service year changes.
        runInInjectionContext(this.injector, () => {
          effect(() => {
            if (this.appStore.serviceYear() !== null) {
              this.getOptionData();
            }
          });
        });
      } else {
        this.getOptionData();
      }
    }
  }

  getOptionData() {
    this.fetchOptions().subscribe(result => {
      this.setOptionData(result);
      this.ready.emit();
      this.isReady.set(true);
    });
  }

  // Refetches the options and keeps the selection; if every payer was checked, new payers are checked too.
  refreshOptions() {
    this.fetchOptions().subscribe(result => {
      const wasAll = this.allToggled();
      const selectedIDs = new Set(this.selected().map(o => o.ID));

      this.setOptionData(result);

      const selected = wasAll ? this.options : this.options.filter(o => selectedIDs.has(o.ID));
      this.control.setValue(this.multiple ? [...selected] : (selected[0] ?? null));
    });
  }

  setOptionData(payerOptions: PayerItem[]) {
    this.options = payerOptions ?? [];
    this.applySearch();
  }

  // null clears, [] checks every payer, otherwise selects by ID.
  setSelectedValues(values: PayerItem[] | null) {
    if (!values) {
      this.control.reset();
      this.control.setValue(this.multiple ? [] : null);
      this.filterControl.setValue('');
      this.hasSelection.set(false);
      return;
    }

    if (values.length == 0) {
      this.toggleAll(true);
      return;
    }

    const ids = new Set(values.map(v => v.ID));
    const matches = this.options.filter(o => ids.has(o.ID));
    this.control.setValue(this.multiple ? matches : (matches[0] ?? null));
    this.hasSelection.set(matches.length > 0);
  }

  // [{ID: 0}] = every payer.
  getValues(): PayerItem[] {
    if (this.allToggled()) {
      return [{ ID: 0 } as PayerItem];
    }

    return this.selected();
  }

  // The select-all checkbox: a user change, so the control is marked dirty.
  onToggleAll(checked: boolean) {
    this.toggleAll(checked);
    this.control.markAsDirty();
  }

  toggleAll(checked: boolean) {
    if (!this.multiple) {
      return;
    }

    this.control.setValue(checked ? [...this.options] : []);
    this.hasSelection.set(checked && this.options.length > 0);
  }

  allToggled(): boolean {
    return this.multiple && this.options.length > 0 && this.selected().length === this.options.length;
  }

  indeterminate(): boolean {
    const count = this.selected().length;
    return this.multiple && count > 0 && count < this.options.length;
  }

  summary(): string {
    const selected = this.selectedInOptionOrder();

    if (selected.length <= 1) {
      return selected[0]?.Abbreviation ?? '';
    }

    return this.allToggled() ? 'All' : `${selected.length} Selected`;
  }

  tooltip(): string {
    const selected = this.selectedInOptionOrder();
    return selected.length > 1 ? selected.map(o => o.Abbreviation).join(', ') : '';
  }

  onClosed() {
    this.valueChanged.emit(this.control.value);
    this.hasSelection.set(this.selected().length > 0);
  }

  markAsTouched() {
    this.control.markAsTouched();
  }

  private selected(): PayerItem[] {
    const value = this.control.value;

    if (this.multiple) {
      return value ?? [];
    }

    return value ? [value] : [];
  }

  private selectedInOptionOrder(): PayerItem[] {
    const ids = new Set(this.selected().map(o => o.ID));
    return this.options.filter(o => ids.has(o.ID));
  }

  // Search matches the abbreviation.
  private applySearch() {
    const search = this.filterControl.value.trim().toLowerCase();
    this.filteredOptions = search
      ? this.options.filter(o => (o.Abbreviation ?? '').toLowerCase().includes(search))
      : this.options.slice();
  }

  private fetchOptions(): Observable<PayerItem[]> {
    if (this.filterByMORMap) {
      return this.filterService.getMORPayerOptions();
    }

    if (this.filterByAsmOnServiceYear) {
      return this.filterService.getASMPayerOptions(this.appStore.serviceYear() ?? 0);
    }

    return this.filterService.getPayerOptions(this.includeInactive);
  }
}
